import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  SubmitSurveyRequestPayload,
  SurveyAnswerPayload,
  usePublicSurvey,
  useSubmitSurveyResponse,
} from '@/api';
import { useResponsive } from '@/hooks/useResponsive';
import useWebview from '@/hooks/V3/useWebview';
import QuestionCard from './components/QuestionCard';
import SurveyDialog from './components/SurveyDialog';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';

type SurveyAnswers = Record<number, string | string[]>;

const SurveyPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cplatTokenFromUrl = searchParams.get('cplatToken');
  const user = useAppSelector((state) => state.user);

  // 쿼리스트링 > Redux 순으로 토큰 우선순위 결정
  const cplatToken = cplatTokenFromUrl || user.CplatToken;

  const { data: survey, isLoading, error } = usePublicSurvey(id);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  useResponsive(); // PC 사이즈 여부는 현재 사용하지 않지만, 향후 확장을 위해 훅 호출만 유지
  const { postMessage } = useWebview();
  const submitMutation = useSubmitSurveyResponse();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [isDialogError, setIsDialogError] = useState(false);
  const [dialogTextAlign, setDialogTextAlign] = useState<'left' | 'center'>('center');
  const [dialogListItems, setDialogListItems] = useState<string[]>([]);

  const openDialog = (
    title: string,
    message: string,
    isError = false,
    textAlign: 'left' | 'center' = 'center',
    listItems: string[] = [],
  ) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setIsDialogError(isError);
    setDialogTextAlign(textAlign);
    setDialogListItems(listItems);
    setDialogOpen(true);
  };

  // body 배경색 설정 (배경 이미지 없을 때)
  useEffect(() => {
    if (!survey) return;

    const originalBgColor = document.body.style.backgroundColor;

    if (!survey.backgroundImageUrl) {
      document.body.style.backgroundColor = '#fff';
    }

    return () => {
      document.body.style.backgroundColor = originalBgColor;
    };
  }, [survey]);

  // 에러 시 카운트다운 완료 후 리다이렉트
  const handleErrorRedirect = useCallback(() => {
    if (window.ReactNativeWebView) {
      // 웹뷰: 네이티브 스택 뒤로 가기 (웹뷰 닫기)
      postMessage({
        action: 'backApp',
        data: 'backApp',
      });
    } else {
      // 일반 웹: 대시보드로 이동
      navigate(ROUTES.dashboard);
    }
  }, [postMessage, navigate]);

  // 단일 선택 (RADIO)
  const handleRadioChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  // 다중 선택 (CHECKBOX)
  const handleCheckboxChange = (questionId: number, value: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = (prev[questionId] as string[]) || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, value] };
      } else {
        return { ...prev, [questionId]: current.filter((v) => v !== value) };
      }
    });
  };

  // 텍스트 입력 (TEXT)
  const handleTextChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  // 제출
  const handleSubmit = () => {
    if (!survey) return;

    if (!cplatToken) {
      openDialog('제출 불가', '토큰 정보가 없어 설문을 제출할 수 없습니다.\n앱에서 다시 접속해 주세요.', false, 'left');
      return;
    }

    // 필수 문항 응답 여부 검증
    const missingRequiredQuestions: string[] = [];

    survey.questions.forEach((question) => {
      if (!question.isRequired) return;

      const value = answers[question.questionId];

      // 응답 자체가 없는 경우
      if (value === undefined || value === null) {
        missingRequiredQuestions.push(question.questionText);
        return;
      }

      if (question.questionType === 'RADIO') {
        const optionId = Number(value as string);
        if (Number.isNaN(optionId)) {
          missingRequiredQuestions.push(question.questionText);
        }
        return;
      }

      if (question.questionType === 'CHECKBOX') {
        // 옵션이 없는 체크박스 문항은 (데이터 오류 가능성) 검증에서 제외
        if (!question.options || question.options.length === 0) {
          return;
        }

        const list = value as string[];
        if (!Array.isArray(list) || list.length === 0) {
          missingRequiredQuestions.push(question.questionText);
        }
        return;
      }

      if (question.questionType === 'TEXTAREA') {
        const text = String(value ?? '').trim();
        if (!text) {
          missingRequiredQuestions.push(question.questionText);
        }
      }
    });

    if (missingRequiredQuestions.length > 0) {
      openDialog(
        '필수 문항 확인',
        '다음 필수 문항을 모두 입력해 주세요.',
        false,
        'left',
        missingRequiredQuestions,
      );
      return;
    }

    const answerList: SurveyAnswerPayload[] = survey.questions.reduce<SurveyAnswerPayload[]>(
      (acc, question) => {
        const value = answers[question.questionId];
        if (value === undefined || value === null) return acc;

        if (question.questionType === 'RADIO') {
          const optionId = Number(value as string);
          if (Number.isNaN(optionId)) return acc;
          acc.push({
            questionId: question.questionId,
            selectedOptionId: optionId,
          });
          return acc;
        }

        if (question.questionType === 'CHECKBOX') {
          const optionIds = (value as string[])
            .map((v) => Number(v))
            .filter((n) => !Number.isNaN(n));

          if (optionIds.length === 0) return acc;

          acc.push({
            questionId: question.questionId,
            selectedOptionIds: optionIds,
          });
          return acc;
        }

        if (question.questionType === 'TEXTAREA') {
          const text = String(value ?? '').trim();
          if (!text) return acc;

          acc.push({
            questionId: question.questionId,
            answerText: text,
          });
          return acc;
        }

        return acc;
      },
      [],
    );

    const payload: SubmitSurveyRequestPayload = {
      cplatToken,
      answers: answerList,
    };

    submitMutation.mutate(
      {
        surveyId: survey.surveyId,
        payload,
      },
      {
        onSuccess: () => {
          // 완료 다이얼로그 노출 (3초 후 메인으로 이동 또는 웹뷰 닫기)
          openDialog('제출 완료', '설문이 제출되었습니다. 감사합니다.', true, 'left');
        },
        onError: (error: unknown) => {
          // axios 에러에서 status code 추출
          const status =
            error && typeof error === 'object' && 'response' in error
              ? (error as { response?: { status?: number } }).response?.status
              : undefined;

          switch (status) {
            case 409:
              // 중복 제출
              openDialog('이미 제출됨', '이미 설문에 응답하셨습니다.\n중복 응답이 허용되지 않는 설문입니다.', true, 'left');
              break;
            case 401:
              // 인증 실패
              openDialog('인증 실패', '인증 정보가 유효하지 않습니다.\n앱에서 다시 접속해 주세요.', true, 'left');
              break;
            case 404:
              // 설문 없음 또는 비활성
              openDialog('설문 없음', '존재하지 않거나 종료된 설문입니다.', true, 'left');
              break;
            case 400:
              // 필수 답변 누락 등 (입력 오류는 사용자가 수정 가능하므로 에러 리다이렉트 안함)
              openDialog('입력 오류', '요청이 올바르지 않습니다.\n필수 항목을 모두 입력해 주세요.', false, 'left');
              break;
            default:
              openDialog('제출 실패', '설문 제출 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주세요.', true, 'left');
          }
        },
      },
    );
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  // 에러 상태
  if (error || !survey) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-white px-4">
        <p className="text-lg font-medium text-gray-900">설문을 찾을 수 없습니다</p>
        <p className="mt-2 text-sm text-gray-500">존재하지 않거나 종료된 설문입니다.</p>
      </div>
    );
  }

  return (
    <>
      {/* iOS Safari 호환 고정 배경 */}
      {survey.backgroundImageUrl && (
        <div
          className="fixed inset-0 -z-10"
          style={{
            backgroundImage: `url(${survey.backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}
      <div className="flex min-h-screen w-full flex-col pt-[10px] pb-[60px] md:pt-[60px]">
      {/* Header Image */}
      {survey.headerImageUrl && (
        <div className="mx-auto mb-2 w-full max-w-[382px]">
          <img src={survey.headerImageUrl} alt="Survey header" className="w-full" />
        </div>
      )}

      {/* Content */}
      <main className={`flex flex-1 flex-col px-5 `}>
        <div className="mx-auto w-full max-w-[820px] ">
          {/* Questions */}
          <div className="space-y-4">
            {survey.questions.map((question, idx) => (
              <QuestionCard
                key={question.questionId}
                question={question}
                index={idx + 1}
                value={answers[question.questionId]}
                onRadioChange={handleRadioChange}
                onCheckboxChange={handleCheckboxChange}
                onTextChange={handleTextChange}
              />
            ))}
          </div>

        <div className='mx-auto w-full max-w-[780px] mt-[10px]'>
          {/* Description */}
          {survey.description && (
            <p className=" max-w-[780px] whitespace-pre-wrap font-GmarketSans text-xs font-medium leading-[140%] text-black">
              {survey.description}
            </p>
          )}
          </div>

          {/* Submit Button */}
          <div className="mx-auto mt-8 w-full max-w-[345px] px-5">
            {survey.submitButtonImageUrl ? (
              <button className="w-full" onClick={handleSubmit} disabled={submitMutation.isPending}>
                <img src={survey.submitButtonImageUrl} alt="제출" className="w-full" />
              </button>
            ) : (
              <button
                className="w-full rounded-lg bg-primary-500 py-3 font-medium text-white hover:bg-primary-600 active:bg-primary-700 disabled:opacity-60"
                onClick={handleSubmit}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending ? '제출 중...' : '제출하기'}
              </button>
            )}
          </div>
        </div>
      </main>
      <SurveyDialog
        open={dialogOpen}
        title={dialogTitle}
        message={dialogMessage}
        onOpenChange={setDialogOpen}
        isError={isDialogError}
        countdownSeconds={3}
        onCountdownEnd={handleErrorRedirect}
        textAlign={dialogTextAlign}
        listItems={dialogListItems}
      />
    </div>
    </>
  );
};

export default SurveyPage;
